import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import { isEqual } from "lodash";
import { ReloadOutlined } from "@ant-design/icons";
import { getPipelineLogs } from "../../../utils/api";
import {
  Button,
  DatePicker,
  Empty,
  Input,
  message,
  Space,
  Table,
  Tag,
  Select,
} from "antd";
import Dropdowns from "./Dropdowns";
import { Footer, LoadingIcon, Sidebar } from "../../../components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const PipelineLogs = () => {
  const { userData } = useContext(UserContext);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [currentSite, setCurrentSite] = useState(null);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [pipelineLogStatus, setPipelineLogStatus] = useState("");
  const [pipelineLogsData, setPipelineLogsData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([
    "completed",
    "started",
    "initiated",
    "error",
  ]);

  // Initialize logStartDate and logEndDate as dates without times
  const [logStartDate, setLogStartDate] = useState(dayjs().subtract(15, "day"));
  const [logEndDate, setLogEndDate] = useState(dayjs());

  const filteredData = pipelineLogsData.filter((item) => {
    const itemText = Object.values(item).join(" ").toLowerCase();
    const matchesSearchText = itemText.includes(searchText.toLowerCase());

    const itemStatus = (item.Status || "").toLowerCase();
    const matchesStatus = selectedStatuses.some((status) =>
      itemStatus.includes(status),
    );

    return matchesSearchText && matchesStatus;
  });

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (
      currentOrg &&
      currentSite &&
      currentCrop &&
      logStartDate &&
      logEndDate
    ) {
      fetchPipelineLogs();
    }
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    logStartDate,
    logEndDate,
    pipelineLogStatus,
  ]);

  useEffect(() => {
    if (
      currentOrg &&
      currentSite &&
      currentCrop &&
      logStartDate &&
      logEndDate
    ) {
      const interval = setInterval(() => {
        fetchPipelineLogs(true);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [
    currentOrg,
    currentSite,
    currentCrop,
    logStartDate,
    logEndDate,
    pipelineLogStatus,
  ]);

  const fetchPipelineLogs = async (isAutoUpdate = false) => {
    if (!isAutoUpdate) {
      setLoading(true);
    }
    try {
      const res = await getPipelineLogs(
        currentSite?.id,
        currentCrop?.id,
        logStartDate.startOf("day").toISOString(),
        logEndDate.endOf("day").toISOString(),
        pipelineLogStatus,
      );
      let logsData = res.data.data || [];

      // Sort data by HarvestDate in descending order
      logsData.sort(
        (a, b) =>
          dayjs(b.HarvestDate, "YYMMDD") - dayjs(a.HarvestDate, "YYMMDD"),
      );

      // Compare new data with existing data
      if (!isAutoUpdate) {
        setPipelineLogsData(logsData);
      } else {
        if (!isEqual(logsData, pipelineLogsData)) {
          setPipelineLogsData(logsData);
        }
      }
      // Else, do nothing to keep the current state
    } catch (e) {
      if (!isAutoUpdate) {
        messageApi.open({
          type: "error",
          content: "Failed to fetch the logs",
        });
      }
      // Suppress errors during auto-update
    } finally {
      if (!isAutoUpdate) {
        setLoading(false);
      }
      // Don't change the loading state during auto-update
    }
  };

  const columns = [
    {
      title: "Bay",
      dataIndex: "Bay",
      key: "Bay",
      showSorterTooltip: true,
      sorter: (a, b) => (a.Bay || "").localeCompare(b.Bay || ""),
      filters: pipelineLogsData.map((log) => ({
        text: log.Bay || "N/A",
        value: log.Bay || "N/A",
      })),
      onFilter: (value, record) => (record.Bay || "").includes(value),
      render: (text) => {
        let formattedText = (text || "").replace(/_/g, " ");
        const cropName = currentCrop?.name || "";
        const regex = new RegExp(cropName, "i");
        formattedText = formattedText.replace(regex, "").trim();
        return formattedText || "N/A";
      },
    },
    {
      title: "Crop",
      dataIndex: "Crop",
      key: "Crop",
      showSorterTooltip: true,
      sorter: (a, b) => (a.Crop || "").localeCompare(b.Crop || ""),
      filters: Array.from(
        new Set(pipelineLogsData.map((log) => (log.Crop || "").toLowerCase())),
      ).map((uniqueCrop) => {
        const originalCrop = pipelineLogsData.find(
          (log) => (log.Crop || "").toLowerCase() === uniqueCrop,
        )?.Crop;
        return { text: originalCrop || "N/A", value: uniqueCrop };
      }),
      onFilter: (value, record) =>
        (record.Crop || "").toLowerCase().includes(value),
      render: (text) => {
        return text
          ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
          : "N/A";
      },
    },
    {
      title: "Harvest Date",
      dataIndex: "HarvestDate",
      key: "HarvestDate",
      showSorterTooltip: true,
      sorter: (a, b) =>
        dayjs(a.HarvestDate, "YYMMDD") - dayjs(b.HarvestDate, "YYMMDD"),
      render: (text) =>
        text ? dayjs(text, "YYMMDD").format("Do MMM, YYYY") : "N/A",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                const [startDate, endDate] = dates;
                setSelectedKeys([
                  `${startDate.format("YYMMDD")},${endDate.format("YYMMDD")}`,
                ]);
              } else {
                setSelectedKeys([]);
              }
            }}
            format="Do MMM, YYYY"
            value={
              selectedKeys[0]
                ? [
                    dayjs(selectedKeys[0].split(",")[0], "YYMMDD"),
                    dayjs(selectedKeys[0].split(",")[1], "YYMMDD"),
                  ]
                : null
            }
          />
          <Space
            style={{
              marginTop: 8,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
              }}
              size="small"
              style={{ width: 70 }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={confirm}
              size="small"
              style={{ width: 70 }}
            >
              Filter
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        if (!value) return true;
        const [start, end] = value.split(",");
        const recordDate = dayjs(record.HarvestDate, "YYMMDD");
        return recordDate.isBetween(
          dayjs(start, "YYMMDD"),
          dayjs(end, "YYMMDD"),
          null,
          "[]",
        );
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      showSorterTooltip: true,
      sorter: (a, b) => (a.Status || "").localeCompare(b.Status || ""),
      render: (text) => {
        let color = "default";
        if (text) {
          if (text.toLowerCase().includes("completed")) color = "success";
          else if (
            text.toLowerCase().includes("failed") ||
            text.toLowerCase().includes("error")
          )
            color = "error";
          else if (text.toLowerCase().includes("started")) color = "processing";
          else if (text.toLowerCase().includes("initiated")) color = "warning";
        }
        return <Tag color={color}>{text || "N/A"}</Tag>;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Filter by status"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Button
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
              }}
              size="small"
              style={{ width: 70 }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={confirm}
              size="small"
              style={{ width: 70 }}
            >
              Filter
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record.Status || "").toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Remark",
      dataIndex: "Remark",
      key: "Remark",
      render: (text) => text || "N/A",
    },
  ];

  const handleRefresh = () => {
    fetchPipelineLogs();
  };

  return (
    <>
      <div id="dashboard">
        <Sidebar
          active="pipelineLogs"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex flex-col w-full px-20 ${sidebarOpen ? "pl-60 sm:pl-10" : "sm:pl-14"} pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-4 justify-center transition-padding ease-in-out duration-200`}
        >
          <Dropdowns
            currentOrg={currentOrg}
            setCurrentOrg={setCurrentOrg}
            currentSite={currentSite}
            setCurrentSite={setCurrentSite}
            currentCrop={currentCrop}
            setCurrentCrop={setCurrentCrop}
            setSiteMetadata={setSiteMetadata}
            logStartDate={logStartDate}
            dropdownLoading={dropdownLoading}
            setDropdownLoading={setDropdownLoading}
            setLogStartDate={setLogStartDate}
            logEndDate={logEndDate}
            setLogEndDate={setLogEndDate}
            pipelineLogStatus={pipelineLogStatus}
            setPipelineLogStatus={setPipelineLogStatus}
          />
          <div className="flex flex-row items-center space-x-4 mb-5 sm:flex-col sm:items-stretch sm:space-x-0 sm:space-y-2">
            <Input
              placeholder="Search logs"
              value={searchText}
              onChange={handleSearch}
              className="sm:mb-2 sm:mr-0"
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Select Status"
              value={selectedStatuses}
              onChange={(value) => {
                // If no statuses are selected, default to all
                if (value.length === 0) {
                  setSelectedStatuses([
                    "completed",
                    "started",
                    "initiated",
                    "error",
                  ]);
                } else {
                  setSelectedStatuses(value);
                }
              }}
              style={{ minWidth: 200 }}
              options={[
                { value: "completed", label: "Completed" },
                { value: "started", label: "Started" },
                { value: "initiated", label: "Initiated" },
                { value: "error", label: "Error" },
              ]}
              className="sm:mb-2 sm:mr-0"
            />
            <Button
              type="primary"
              onClick={handleRefresh}
              className="rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105 sm:mb-2"
            >
              Refresh <ReloadOutlined />
            </Button>
          </div>

          {loading || dropdownLoading ? (
            <LoadingIcon height="200px"></LoadingIcon>
          ) : pipelineLogsData && pipelineLogsData.length > 0 ? (
            <Table
              dataSource={filteredData}
              columns={columns}
              pagination={{
                hideOnSinglePage: true,
                position: ["bottomCenter"],
              }}
              className="mb-5 w-full text-center justify-center"
              rowClassName={(record) =>
                record.Status && record.Status.toLowerCase().includes("error")
                  ? "bg-dangerRed/10"
                  : ""
              }
              scroll={{ x: 768 }}
              loading={loading}
            />
          ) : (
            <Empty
              description="No Logs present."
              className="absolute top-2/5 left-9/20"
            ></Empty>
          )}
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default PipelineLogs;
